import { formatReferenceCopy } from 'utils/typography'
import './tnved-check-products-panel.scss'

import {
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material'

import ModalBig from '../../../../common/modalBig'
import TnvedProductsTable from './tnved-products-table'

import {
  COLORS,
} from 'config/constants'
import {
  formatHSCode,
} from 'utils/helpers'

import {
  buildTnvedReviewDrafts,
  normalizeTnvedCode,
  tnvedReviewsHaveCodeChanges,
  useApplicationOrderProducts,
} from '../tnved-review-utils'
import {
  formatApplicationActionError,
} from '../reveal-contacts-form'

const TnvedCheckProductsPanel = ({
  orderId,
  reviews,
  changesConfirmed = false,
  readOnly = false,
  managerView = false,
  runAction,
  onSaveReviews,
  onConfirmChanges,
}: ApplicationTnvedCheckProductsPanelProps) => {
  const { t } = useTranslation()
  const { products, loading } = useApplicationOrderProducts(orderId)
  const [ drafts, setDrafts ] = useState<ApplicationTnvedReview[]>([ ])
  const [ editingProductId, setEditingProductId ] = useState<number | null>(null)
  const [ modalCode, setModalCode ] = useState('')
  const [ modalReason, setModalReason ] = useState('')
  const [ confirmModalOpen, setConfirmModalOpen ] = useState(false)
  const [ confirmInProgress, setConfirmInProgress ] = useState(false)
  const [ saveInProgress, setSaveInProgress ] = useState(false)
  const [ saveError, setSaveError ] = useState<string | null>(null)
  const [ confirmError, setConfirmError ] = useState<string | null>(null)

  useEffect(() => {
    if (!products.length) {
      return
    }
    setDrafts(buildTnvedReviewDrafts(products, reviews))
  }, [ products, reviews ])

  const sortedDrafts = useMemo(() => (
    drafts.slice().sort((left, right) => (
      left.invoice_id - right.invoice_id || left.product_name.localeCompare(right.product_name, 'ru')
    ))
  ), [ drafts ])

  const hasCodeChanges = useMemo(
    () => tnvedReviewsHaveCodeChanges(sortedDrafts),
    [ sortedDrafts ],
  )
  const needsConfirmation = hasCodeChanges && !changesConfirmed

  const editingEntry = editingProductId != null
    ? sortedDrafts.find((entry) => entry.product_id === editingProductId) ?? null
    : null

  const openEditModal = (entry: ApplicationTnvedReview) => {
    if (saveInProgress) {
      return
    }
    setSaveError(null)
    setEditingProductId(entry.product_id)
    setModalCode(normalizeTnvedCode(entry.code))
    setModalReason(entry.change_reason)
  }

  const closeEditModal = () => {
    if (saveInProgress) {
      return
    }
    setEditingProductId(null)
    setModalCode('')
    setModalReason('')
  }

  const handleModalConfirm = () => {
    if (editingProductId == null || saveInProgress || !onSaveReviews) {
      return
    }
    const code = normalizeTnvedCode(modalCode)
    if (!code) {
      return
    }
    const nextDrafts = drafts.map((entry) => (
      entry.product_id === editingProductId
        ? { ...entry, code, change_reason: modalReason.trim() }
        : entry
    ))
    setSaveError(null)
    setSaveInProgress(true)
    const run = async () => {
      try {
        if (runAction) {
          await runAction(() => onSaveReviews(nextDrafts), 'applications__message__tnvedSaved')
        } else {
          await onSaveReviews(nextDrafts)
        }
        setDrafts(nextDrafts)
        setEditingProductId(null)
        setModalCode('')
        setModalReason('')
      } catch (e: unknown) {
        const err = e as { data?: unknown }
        setSaveError(formatApplicationActionError({
          data: err?.data,
          fallback: formatReferenceCopy(t('applications__text__actionError')),
        }))
      } finally {
        setSaveInProgress(false)
      }
    }
    void run()
  }

  const handleConfirmChanges = () => {
    if (!onConfirmChanges || confirmInProgress) {
      return
    }
    setConfirmError(null)
    setConfirmInProgress(true)
    const run = async () => {
      try {
        if (runAction) {
          await runAction(async () => {
            await onConfirmChanges()
          }, 'applications__message__tnvedConfirmed')
        } else {
          await onConfirmChanges()
        }
        setConfirmModalOpen(false)
      } catch (e: unknown) {
        const err = e as { data?: unknown }
        setConfirmError(formatApplicationActionError({
          data: err?.data,
          fallback: formatReferenceCopy(t('applications__text__actionError')),
        }))
      } finally {
        setConfirmInProgress(false)
      }
    }
    void run()
  }

  const closeConfirmModal = () => {
    if (confirmInProgress) {
      return
    }
    setConfirmModalOpen(false)
  }

  if (!orderId) {
    return null
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={18} />
        <Typography variant='body2' sx={{ color: managerView ? 'text.primary' : COLORS.LIGHT_TEXT_SECONDARY }}>
          {formatReferenceCopy(t('applications__text__tnvedProductsLoading'))}
        </Typography>
      </Box>
    )
  }

  if (!sortedDrafts.length) {
    return (
      <Typography variant='body2' sx={{ color: managerView ? 'text.primary' : COLORS.LIGHT_TEXT_SECONDARY }}>
        {formatReferenceCopy(t('applications__text__tnvedProductsEmpty'))}
      </Typography>
    )
  }

  return (
    <Box className='application-tnved-check-products-panel'>
      <TnvedProductsTable
        entries={sortedDrafts}
        readOnly={readOnly}
        managerView={managerView}
        saving={saveInProgress}
        onEdit={openEditModal}
      />

      {needsConfirmation && onConfirmChanges && (
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          mt: 1.5,
          pt: 0.5,
        }}>
          <Typography variant='body2' sx={{ color: managerView ? 'text.primary' : COLORS.LIGHT_TEXT_SECONDARY }}>
            {formatReferenceCopy(t('applications__text__tnvedConfirmChangesHint'))}
          </Typography>
          <Button
            variant='contained'
            size='small'
            onClick={() => {
              setConfirmError(null)
              setConfirmModalOpen(true)
            }}
          >
            <Typography component='span' variant='inherit'>
              {formatReferenceCopy(t('applications__text__tnvedConfirmChangesButton'))}
            </Typography>
          </Button>
        </Box>
      )}

      <ModalBig
        isOpen={editingEntry != null}
        onClose={closeEditModal}
        title={formatReferenceCopy(t('applications__text__tnvedChangeModalTitle', {
          product: editingEntry?.product_name || '',
        }))}
        width={736}
        buttons={(
          <div className='application-tnved-check-products-panel__modal-actions'>
            <Button
              variant='outlined'
              sx={{ minWidth: '238px', marginRight: 3, flexShrink: 0 }}
              disabled={saveInProgress}
              onClick={closeEditModal}
            >
              <Typography component='span' variant='inherit'>
                {formatReferenceCopy(t('actions__text__cancel'))}
              </Typography>
            </Button>
            <Button
              variant='contained'
              fullWidth
              disabled={!modalCode.trim() || saveInProgress}
              onClick={handleModalConfirm}
              startIcon={saveInProgress ? <CircularProgress size={18} color='inherit' /> : undefined}
            >
              <Typography component='span' variant='inherit'>
                {saveInProgress
                  ? formatReferenceCopy(t('applications__text__tnvedSaveCodeInProgress'))
                  : formatReferenceCopy(t('actions__text__save'))}
              </Typography>
            </Button>
          </div>
        )}
      >
        {editingEntry && (
          <div className='application-tnved-check-products-panel__modal-body'>
            {saveError && (
              <Alert severity='error' sx={{ mb: 2 }}>
                <Typography color={managerView ? 'text.primary' : undefined} variant='body2'>{formatReferenceCopy(saveError)}</Typography>
              </Alert>
            )}
            <Typography variant='body2' sx={{ mb: 2, color: 'text.primary' }}>
              {formatReferenceCopy(t('applications__text__tnvedCurrentCode'))}: {formatHSCode(normalizeTnvedCode(editingEntry.original_code)) || '—'}
            </Typography>
            <TextField
              InputLabelProps={{ shrink: true }}
              fullWidth
              size='small'
              sx={{ mb: 2 }}
              label={(
                <Typography component='span' variant='inherit'>
                  {formatReferenceCopy(t('common__text__codeOfHS'))}
                </Typography>
              )}
              value={formatHSCode(modalCode)}
              onChange={(event) => setModalCode(normalizeTnvedCode(event.target.value))}
              disabled={saveInProgress}
            />
            <TextField
              InputLabelProps={{ shrink: true }}
              fullWidth
              size='small'
              multiline
              minRows={3}
              label={(
                <Typography component='span' variant='inherit'>
                  {formatReferenceCopy(t('applications__text__tnvedChangeReason'))}
                </Typography>
              )}
              placeholder={formatReferenceCopy(t('applications__text__tnvedChangeReasonPlaceholder'))}
              value={modalReason}
              onChange={(event) => setModalReason(event.target.value)}
              disabled={saveInProgress}
            />
          </div>
        )}
      </ModalBig>

      <ModalBig
        isOpen={confirmModalOpen}
        onClose={closeConfirmModal}
        title={formatReferenceCopy(t('applications__text__tnvedConfirmChangesModalTitle'))}
        width={736}
        buttons={(
          <div className='application-tnved-check-products-panel__modal-actions'>
            <Button
              variant='outlined'
              sx={{ minWidth: '238px', marginRight: 3, flexShrink: 0 }}
              disabled={confirmInProgress}
              onClick={closeConfirmModal}
            >
              <Typography component='span' variant='inherit'>
                {formatReferenceCopy(t('actions__text__cancel'))}
              </Typography>
            </Button>
            <Button
              variant='contained'
              fullWidth
              disabled={confirmInProgress}
              onClick={handleConfirmChanges}
              startIcon={confirmInProgress ? <CircularProgress size={18} color='inherit' /> : undefined}
            >
              <Typography component='span' variant='inherit'>
                {confirmInProgress
                  ? formatReferenceCopy(t('applications__text__tnvedConfirmChangesInProgress'))
                  : formatReferenceCopy(t('applications__text__tnvedConfirmChangesButton'))}
              </Typography>
            </Button>
          </div>
        )}
      >
        {confirmInProgress && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <CircularProgress size={22} />
            <Typography variant='body2' sx={{ color: managerView ? 'text.primary' : COLORS.LIGHT_TEXT_SECONDARY }}>
              {formatReferenceCopy(t('applications__text__tnvedConfirmChangesInProgress'))}
            </Typography>
          </Box>
        )}
        {confirmError && (
          <Alert severity='error' sx={{ mb: 2 }}>
            <Typography color={managerView ? 'text.primary' : undefined} variant='body2'>{formatReferenceCopy(confirmError)}</Typography>
          </Alert>
        )}
        <Typography color='text.primary' variant='body2' className='application-tnved-check-products-panel__confirm-modal-text'>
          {formatReferenceCopy(t('applications__text__tnvedConfirmChangesModalBody'))}
        </Typography>
      </ModalBig>
    </Box>
  )
}

export default TnvedCheckProductsPanel
